import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { createUserWithEmailAndPassword, deleteUser, signInWithEmailAndPassword } from 'firebase/auth';
import { Typography, TextField, TextFieldProps, Box, Button, Avatar, Stack, Grid2, IconButton, InputAdornment, Link } from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { auth, db } from '../../firebase/firebase';
import { addEmployee, addEmployeeWorkHours, fetchEmployeeSignUpInvite, updateEmployeeScheduleForDate, uploadImageToStorage, generateRecurringBreakBlockersForEmployee } from '../../firebase/firebase-delegate';
import { generateScheduleBitString, extractTimesFromBitString } from '../../time/timeUtils';
import { WorkHours, Employee, BitStringData, BusinessConfig, RecurringBreak, createEmptyBusinessConfig } from '../../types';
import { arrayUnion, doc, getDoc, updateDoc } from 'firebase/firestore';
import { BUFFER_PERIOD, DAYJS_DAYS_OF_WEEK } from '../../constants';
import ImageSelect from '../../components/global/images/ImageSelector';
import ScheduleCard from '../../components/employee/services/ScheduleCard';
import { useMutation, useQuery } from '@tanstack/react-query';
import ErrorDialog from '../../components/global/ErrorDialog';
import { callSetWebsiteClaim } from '../../firebase/cloud-function-helper';
import { tenantRootPath, tenantStoragePath } from '../../tenancy/paths';
import { useTenant } from '../../tenancy/TenantContext';
import { dayjs } from '../../utils/dayjs-config';
import LoadingSpinner from '../../components/global/LoadingSpinner';
import { validateForm } from '../../components/booking/ConfirmAppointment';
import PhoneInput from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import { normalizeEmail } from '../../utils/emailUtils';
import { convertBusinessHoursToWorkHours } from '../../utils/businessHours';
import { getHomepageRuntimeConfig } from '../../sitePublishing/publicConfigSource';
import { addTenantQueryToPath, resolveInvitationLandingPath } from '../../auth/adminAccess';
import { useAuth } from '../../contexts/AuthContext';
import { redeemInvitationWithAuthAccount } from '../../auth/invitationRedemption';
import { signupErrorMessage } from '../../auth/signupErrorMessage';
import AuthShell, { useBusinessLogoUrl } from '../../components/auth/AuthShell';

const SignUpPage: React.FC = () => {
    const { tenantId } = useTenant();
    const { user: authenticatedUser, loading: authLoading } = useAuth();
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [showErrorDialog, setShowErrorDialog] = useState(false);
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [phone, setPhone] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [admin, setAdmin] = useState(false);
    const [formLoading, setFormLoading] = useState(false);
    const [inviteCode, setInviteCode] = useState<string | null>(null);
    const [inviteData, setInviteData] = useState<any | null>(null);
    // What sign-up is doing right now, shown while it runs so it never looks frozen.
    const [signUpStep, setSignUpStep] = useState('Creating your account');
    const [recurringBreakTemplates, setRecurringBreakTemplates] = useState<Record<keyof WorkHours, RecurringBreak[]>>(() =>
        DAYJS_DAYS_OF_WEEK.reduce((acc, day) => {
            acc[day] = [];
            return acc;
        }, {} as Record<keyof WorkHours, RecurringBreak[]>)
    );
    const [profilePicChangePlaceHolder, setProfilePicChangePlaceHolder] = useState<File | undefined>(undefined);
    const templateEndDate = dayjs().add(3, 'month');

    const flattenRecurringBreaks = () =>
        DAYJS_DAYS_OF_WEEK.flatMap((day, index) =>
            (recurringBreakTemplates[day] || []).map((definition) => ({
                ...definition,
                weekday: index,
            }))
        );

    // Error messages
    const [errors, setErrors] = useState<{ firstName?: string; lastName?: string; email?: string; phone?: string; password?: string; }>({});

    const location = useLocation();
    const navigate = useNavigate();

    const businessConfigQuery = useQuery<BusinessConfig>({
        queryKey: ['publicBusinessConfig', tenantId],
        queryFn: () => getHomepageRuntimeConfig(tenantId).catch(() => createEmptyBusinessConfig()),
    });
    const logoUrl = useBusinessLogoUrl(businessConfigQuery.data?.businessInfo?.logoImagePath);
    const defaultWorkHours = useMemo(
        () => convertBusinessHoursToWorkHours(businessConfigQuery.data?.businessInfo?.businessHours),
        [businessConfigQuery.data?.businessInfo?.businessHours]
    );
    const [schedules, setSchedules] = useState<WorkHours>(defaultWorkHours);
    const [didHydrateBusinessHourDefaults, setDidHydrateBusinessHourDefaults] = useState(false);

    useEffect(() => {
        if (didHydrateBusinessHourDefaults) {
            return;
        }
        if (!businessConfigQuery.data?.businessInfo?.businessHours) {
            return;
        }
        setSchedules(defaultWorkHours);
        setDidHydrateBusinessHourDefaults(true);
    }, [businessConfigQuery.data?.businessInfo?.businessHours, defaultWorkHours, didHydrateBusinessHourDefaults]);

    const verifyInviteCodeMutation = useMutation({
        mutationFn: async () => {
            const urlParams = new URLSearchParams(location.search);
            const code = urlParams.get('code');

            if (!code) {
                throw new Error('Cannot sign up: Missing verification code.'); // 🔴 Ensure this throws
            }

            return await fetchEmployeeSignUpInvite(code, authenticatedUser?.uid ?? null);
        },
        onSuccess: (invitation) => {
            if (invitation.websiteId !== tenantId) {
                setErrorMessage('This invitation belongs to a different site.');
                setShowErrorDialog(true);
                return;
            }
            setAdmin(invitation.isAdmin || false);
            setInviteCode(new URLSearchParams(location.search).get('code') || '');
            setInviteData(invitation);
        },
        onError: (err) => {
            console.error('Failed to verify invite:', err);
            setErrorMessage('Error verifying invite code: ' + (err as Error).message);
            setShowErrorDialog(true);
        },
    });
    const verifyInviteCode = verifyInviteCodeMutation.mutate;

    useEffect(() => {
        if (authLoading) {
            return;
        }
        verifyInviteCode();
    }, [
        authLoading,
        authenticatedUser?.uid,
        location.search,
        tenantId,
        verifyInviteCode,
    ]);

    const addEmployeeMutation = useMutation({
        mutationFn: async () => {
            if (!inviteCode || !inviteData) {
                throw new Error('Invalid invite code.');
            }

            const normalizedEmail = normalizeEmail(email);
            const businessName = businessConfigQuery.data?.businessInfo?.name?.trim();
            setSignUpStep('Creating your account');

            const user = await redeemInvitationWithAuthAccount({
                currentUser: auth.currentUser,
                normalizedEmail,
                createUser: async () =>
                    (await createUserWithEmailAndPassword(auth, normalizedEmail, password)).user,
                signInUser: async () =>
                    (await signInWithEmailAndPassword(auth, normalizedEmail, password)).user,
                deleteUser,
                redeemInvitation: async (userId) => {
                    setSignUpStep(businessName ? `Joining ${businessName}` : 'Accepting your invitation');
                    await callSetWebsiteClaim(userId, inviteCode, {
                        profile: { firstName: firstName.trim(), lastName: lastName.trim(), phone: phone.trim() },
                        workHours: schedules,
                    });
                },
                onDeleteError: (deleteError) => {
                    console.error('Could not remove the account after invitation rejection:', deleteError);
                },
            });
            await user.getIdTokenResult(true);

            const flattenedBreaks = flattenRecurringBreaks();
            const newEmployee: Employee = {
                id: user.uid,
                admin,
                firstName,
                lastName,
                email: normalizedEmail,
                phone,
                appointments: {},
                recurringBreaks: flattenedBreaks,
                active: true,
            };

            if (profilePicChangePlaceHolder) {
                setSignUpStep('Uploading your photo');
                newEmployee.profilePictureImagePath = tenantStoragePath(tenantId, `employees/${newEmployee.id}/profilePicture`);
                await uploadImageToStorage(profilePicChangePlaceHolder, newEmployee.profilePictureImagePath, /*compressionLevel=*/3);
            }

            setSignUpStep('Setting up your schedule');
            await addEmployee(newEmployee, tenantId);
            businessConfigQuery.refetch();
            await addEmployeeWorkHours(newEmployee.id, schedules, tenantId);

            const missingDates = Array.from({ length: BUFFER_PERIOD * 2 + 1 }, (_, i) =>
                dayjs().add(i, 'day')
            );

            await Promise.all(
                missingDates.map(missingDate =>
                    updateEmployeeScheduleForDate(newEmployee.id, schedules, dayjs(missingDate, 'MMDDYYYY'), tenantId)
                )
            );

            const windowStart = dayjs();
            const windowEnd = dayjs().add(BUFFER_PERIOD * 2, 'day');
            await generateRecurringBreakBlockersForEmployee({
                employeeId: newEmployee.id,
                windowStart,
                windowEnd,
                recurringBreakIdsToReset: flattenedBreaks.map((definition) => definition.id),
                siteId: tenantId,
            });

            const serviceIds: string[] = inviteData.services || [];
            if (serviceIds.length) setSignUpStep('Adding your services');

            // Update each service document to include the new employee ID
            await Promise.all(serviceIds.map(async (serviceId) => {
                const serviceRef = doc(db, tenantRootPath(tenantId), 'services', serviceId);
                const serviceSnap = await getDoc(serviceRef);

                if (serviceSnap.exists()) {
                    await updateDoc(serviceRef, { employees: arrayUnion(newEmployee.id) });
                }
            }));

            setSignUpStep('Opening your dashboard');
            return newEmployee;
        },
        onSuccess: () => {
            const landingPath = resolveInvitationLandingPath(
                inviteData?.invitationType,
                admin,
                businessConfigQuery.data?.businessInfo.name
            );
            navigate(addTenantQueryToPath(landingPath, tenantId));
        },
        onError: (err) => {
            console.error('Error completing sign-up:', err);
            setErrorMessage(signupErrorMessage(err));
            setShowErrorDialog(true);
        },
        onSettled: () => {
            setFormLoading(false);
        },
    });


    const isLoading =
        addEmployeeMutation.isPending ||
        verifyInviteCodeMutation.isPending ||
        businessConfigQuery.isLoading ||
        authLoading;

    const isError =
        verifyInviteCodeMutation.isError ||
        businessConfigQuery.isError;

    const handleScheduleChange = (day: keyof WorkHours, updatedSchedule: BitStringData) => {
        setSchedules((prevSchedules) => ({
            ...prevSchedules,
            [day]: generateScheduleBitString(updatedSchedule),
        }));
    };

    const handleRecurringBreaksChange = (day: keyof WorkHours, breaks: RecurringBreak[]) => {
        setRecurringBreakTemplates((prev) => ({
            ...prev,
            [day]: breaks,
        }));
    };

    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        setFormLoading(true);

        const trimmedFirstName = firstName.trim();
        const trimmedLastName = lastName.trim();
        const trimmedEmail = email.trim();
        const trimmedPhone = phone.trim();

        const newErrors: typeof errors = validateForm(trimmedFirstName, trimmedLastName, trimmedEmail, trimmedPhone);
        if (password.length < 6) {
            newErrors.password = 'Use at least 6 characters.';
        }
        setErrors(newErrors);

        if (Object.keys(newErrors).length > 0) {
            setFormLoading(false);
            return;
        }

        addEmployeeMutation.mutate();
    };


    const onImageChange = (imageFile?: File) => {
        setProfilePicChangePlaceHolder(imageFile);
    };

    if (addEmployeeMutation.isPending) {
        return <LoadingSpinner label={signUpStep} detail="Keep this page open. This usually takes under a minute." allowReload={false} />;
    }
    if (isLoading) return <LoadingSpinner label="Checking your invitation" />;

    const businessName = businessConfigQuery.data?.businessInfo?.name?.trim() || '';
    const signInPath = addTenantQueryToPath('/admin', tenantId);

    if (isError) {
        return (
            <AuthShell
                logoUrl={logoUrl}
                businessName={businessName}
                title="We couldn't open this invite"
                subtitle="The link may have expired or already been used. Ask the person who invited you to send a new one."
            >
                <Button href={signInPath} variant="outlined" fullWidth size="large">
                    Go to sign in
                </Button>
                <ErrorDialog open={showErrorDialog} onClose={() => { setShowErrorDialog(false) }} message={errorMessage} />
            </AuthShell>
        );
    }

    return (
        <AuthShell
            maxWidth="md"
            logoUrl={logoUrl}
            businessName={businessName}
            title={businessName ? `Join ${businessName}` : 'Create your account'}
            subtitle={admin
                ? 'You\'ve been invited as an admin. Set up your login and the hours you work.'
                : 'You\'ve been invited to the team. Set up your login and the hours you work.'}
        >
            <Box component="form" onSubmit={handleSubmit} noValidate>
                <Stack spacing={4}>
                    <SignUpSection step={1} title="Your details" detail="This is how customers and your team will see you.">
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3} alignItems={{ xs: 'center', sm: 'flex-start' }}>
                            <Box sx={{ flexShrink: 0, '& > div': { my: 0 } }}>
                                <ImageSelect
                                    onImageChange={onImageChange}
                                    placeholderComponent={<Avatar sx={{ width: 96, height: 96, bgcolor: 'rgba(25,118,210,.1)', color: '#1976d2' }} />}
                                    altText="Profile photo"
                                    circleSize={120}
                                />
                                <Typography variant="caption" color="text.secondary" display="block" align="center" sx={{ mt: 1 }}>
                                    Photo (optional)
                                </Typography>
                            </Box>
                            <Grid2 container spacing={2} sx={{ flex: 1, width: '100%' }}>
                                <Grid2 size={{ xs: 12, sm: 6 }}>
                                    <TextField
                                        label="First name"
                                        value={firstName}
                                        onChange={(e) => setFirstName(e.target.value)}
                                        autoComplete="given-name"
                                        required
                                        fullWidth
                                        error={Boolean(errors.firstName)}
                                        helperText={errors.firstName}
                                    />
                                </Grid2>
                                <Grid2 size={{ xs: 12, sm: 6 }}>
                                    <TextField
                                        label="Last name"
                                        value={lastName}
                                        onChange={(e) => setLastName(e.target.value)}
                                        autoComplete="family-name"
                                        required
                                        fullWidth
                                        error={Boolean(errors.lastName)}
                                        helperText={errors.lastName}
                                    />
                                </Grid2>
                                <Grid2 size={12}>
                                    <Box sx={{ '& .PhoneInput': { display: 'flex', alignItems: 'flex-start', gap: 1.5 }, '& .PhoneInputCountry': { height: 56 } }}>
                                        <PhoneInput
                                            defaultCountry="US"
                                            countries={['US', 'CA']}
                                            international={true}
                                            addInternationalOption={false}
                                            value={phone}
                                            onChange={(value) => setPhone(value || '')}
                                            inputComponent={PhoneTextField}
                                            label="Phone"
                                            required
                                            error={Boolean(errors.phone)}
                                            helperText={errors.phone}
                                        />
                                    </Box>
                                </Grid2>
                                <Grid2 size={12}>
                                    <TextField
                                        label="Email"
                                        type="email"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        autoComplete="email"
                                        error={Boolean(errors.email)}
                                        helperText={errors.email || 'You\'ll use this to sign in.'}
                                        required
                                        fullWidth
                                    />
                                </Grid2>
                                <Grid2 size={12}>
                                    <TextField
                                        label="Password"
                                        type={showPassword ? 'text' : 'password'}
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        autoComplete="new-password"
                                        error={Boolean(errors.password)}
                                        helperText={errors.password || 'At least 6 characters.'}
                                        required
                                        fullWidth
                                        slotProps={{
                                            input: {
                                                endAdornment: (
                                                    <InputAdornment position="end">
                                                        <IconButton
                                                            aria-label={showPassword ? 'Hide password' : 'Show password'}
                                                            onClick={() => setShowPassword((shown) => !shown)}
                                                            edge="end"
                                                        >
                                                            {showPassword ? <VisibilityOff /> : <Visibility />}
                                                        </IconButton>
                                                    </InputAdornment>
                                                ),
                                            },
                                        }}
                                    />
                                </Grid2>
                            </Grid2>
                        </Stack>
                    </SignUpSection>

                    <SignUpSection
                        step={2}
                        title="Your weekly hours"
                        detail={businessName
                            ? `We started you with ${businessName}'s hours. Customers can only book you inside these times. You can change them later.`
                            : 'Customers can only book you inside these times. You can change them later.'}
                    >
                        <Stack spacing={1.5}>
                            {(Object.keys(schedules) as (keyof WorkHours)[]).map((day) => {
                                const parsed = extractTimesFromBitString(schedules[day]);
                                return (
                                    <ScheduleCard
                                        key={day}
                                        day={day}
                                        initialWorkHours={parsed}
                                        onScheduleChange={handleScheduleChange}
                                        recurringBreaks={recurringBreakTemplates[day]}
                                        onRecurringBreaksChange={handleRecurringBreaksChange}
                                        templateEndDate={templateEndDate}
                                    />
                                );
                            })}
                        </Stack>
                    </SignUpSection>

                    <Box>
                        <Button
                            type="submit"
                            variant="contained"
                            size="large"
                            fullWidth
                            disabled={formLoading}
                            sx={{ py: 1.5, fontWeight: 800, bgcolor: '#1976d2', '&:hover': { bgcolor: '#1565c0' } }}
                        >
                            {formLoading ? 'Creating your account…' : 'Create account'}
                        </Button>
                        <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 2 }}>
                            Already have an account?{' '}
                            <Link href={signInPath} fontWeight={700} underline="hover">Sign in</Link>
                        </Typography>
                    </Box>
                </Stack>
            </Box>
            <ErrorDialog open={showErrorDialog} onClose={() => { setShowErrorDialog(false) }} message={errorMessage} />
        </AuthShell>
    );
};

const PhoneTextField = React.forwardRef<HTMLInputElement, TextFieldProps>((props, ref) => (
    <TextField {...props} inputRef={ref} autoComplete="tel" fullWidth />
));
PhoneTextField.displayName = 'PhoneTextField';

interface SignUpSectionProps {
    step: number;
    title: string;
    detail: string;
    children: React.ReactNode;
}

const SignUpSection: React.FC<SignUpSectionProps> = ({ step, title, detail, children }) => (
    <Box component="section" aria-labelledby={`signup-step-${step}`}>
        <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{ mb: 2.5 }}>
            <Box
                aria-hidden
                sx={{
                    width: 28,
                    height: 28,
                    flexShrink: 0,
                    borderRadius: '50%',
                    display: 'grid',
                    placeItems: 'center',
                    bgcolor: '#1976d2',
                    color: '#fff',
                    fontWeight: 800,
                    fontSize: 14,
                    mt: '2px',
                }}
            >
                {step}
            </Box>
            <Box>
                <Typography id={`signup-step-${step}`} variant="h6" component="h2" fontWeight={800} lineHeight={1.3}>
                    {title}
                </Typography>
                <Typography variant="body2" color="text.secondary">{detail}</Typography>
            </Box>
        </Stack>
        {children}
    </Box>
);

export default SignUpPage;
